import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NewsAnalysisOverlay } from "./NewsAnalysisOverlay";
import type { SectorNewsItem, SectorPanelData } from "./HotTopicsSection.types";
import type { MarketRecap } from "../../features/kquant/types";
import { mockApi } from "../../features/kquant/api/mockApi";
import { FormControlStyles } from "../../components/ui/FormControlStyles";
import { Header } from "../../features/kquant/components/Header";
import { HeroSection } from "./HeroSection";
import { HotTopicsSection } from "./HotTopicsSection";
import { MarketAnalysisSection } from "./MarketAnalysisSection";
import { MarketRecapSection } from "./MarketRecapSection";
import { Toast, ToastState } from "../../features/kquant/components/Toast";
import { MapFilter, MarketTab } from "../../features/kquant/components/MarketHeatmapCard";

export default function KQuantSmartInsightsPage() {
  const navigate = useNavigate();
  const [marketTab, setMarketTab] = useState<MarketTab>("Overview");
  const [mapFilter, setMapFilter] = useState<MapFilter>("S&P 500");
  const [recap, setRecap] = useState<MarketRecap | null>(null);

  const [query, setQuery] = useState("");
  const [toast, setToast] = useState<ToastState>(null);
  const [analysisTarget, setAnalysisTarget] = useState<{ news: SectorNewsItem; panel: SectorPanelData } | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rc = await mockApi.getMarketRecap();
        if (!alive) return;
        setRecap(rc.recap);
      } catch (e) {
        if (!alive) return;
        setToast({
          title: "Smart Insights",
          body: "初始化数据失败，请稍后重试。",
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onAnalyze(text: string) {
    const t = (text ?? "").trim();
    if (!t) return;
    setQuery("");
    const seedId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    navigate("/smart-insights/chat", { state: { prompt: t, seedId } });
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-100 text-slate-800">
      <FormControlStyles theme="light" />

      <div className="relative mx-auto max-w-[1280px] px-8">
        <Header />

        <main className="pb-16">
          <HeroSection query={query} setQuery={setQuery} onAnalyze={onAnalyze} />

          <HotTopicsSection
            theme="light"
            onAnalyzeNews={(news, panel) => setAnalysisTarget({ news, panel })}
          />

          <MarketAnalysisSection
            marketTab={marketTab}
            setMarketTab={setMarketTab}
            mapFilter={mapFilter}
            setMapFilter={setMapFilter}
          />

          <MarketRecapSection recap={recap} theme="light" />
        </main>
      </div>

      <Toast
        toast={toast}
        theme="light"
        onClose={() => setToast(null)}
        onPickFollowup={(text) => {
          setQuery(text);
          setToast(null);
        }}
      />
      {analysisTarget && (
        <NewsAnalysisOverlay
          news={analysisTarget.news}
          panel={analysisTarget.panel}
          onClose={() => setAnalysisTarget(null)}
        />
      )}
    </div>
  );
}
